import type { IGlobalSearchStaticEntry } from "../types/global-search";
import {
  EGlobalSearchGate,
  type IGlobalSearchSettingOptionDefinition,
  type IGlobalSearchTabOptionsProps,
} from "../types/global-search-index";
import { buildGlobalSearchSettingOptionEntry } from "./global-search-index-builders";
import {
  GLOBAL_SEARCH_ORGANIZATION_TAB_ENTRIES as ORG_TABS,
  GLOBAL_SEARCH_PROFILE_TAB_ENTRIES as PROFILE_TABS,
} from "./global-search-settings-tabs";

// NOTE: NSAI-6595 — the stable id of every settings control the global search
// index can open. It travels as the SETTINGS target's `setting` param; web
// puts it on the control's wrapper (data-global-search-setting) and mobile on
// its nativeID, and each scrolls to / highlights it once the tab has rendered.
// Changing a value breaks links that are already out there — add, never
// rename. A control shared by two tabs (token usage in profile and
// organization) keeps one id: the tab in the target tells them apart.
export const GLOBAL_SEARCH_SETTING_IDS = {
  // NOTE: Profile → General information
  FULL_NAME: "full_name",
  EMAIL: "email",
  PASSWORD: "password",
  SIGN_IN_METHOD: "sign_in_method",
  WHATSAPP_BOT: "whatsapp_bot",
  DELETE_ACCOUNT: "delete_account",
  // NOTE: Profile → Billing
  BILLING_PLAN: "billing_plan",
  BILLING_CYCLE: "billing_cycle",
  PAYMENT_METHODS: "payment_methods",
  BILLING_HISTORY: "billing_history",
  CANCEL_SUBSCRIPTION: "cancel_subscription",
  // NOTE: Profile → Preferences
  LANGUAGE: "language",
  THEME: "theme",
  TTS_VOICE: "tts_voice",
  AUTO_JOIN_MEETINGS: "auto_join_meetings",
  EMAIL_RECAP: "email_recap",
  NOTIFICATION_CATEGORIES: "notification_categories",
  // NOTE: Profile → About (mobile only, NSAI-5883)
  SERVER: "server",
  APP_VERSION: "app_version",
  // NOTE: Profile → Memory instructions
  MEMORY_INSTRUCTIONS_ENABLED: "memory_instructions_enabled",
  MEMORY_INSTRUCTIONS_TEXT: "memory_instructions_text",
  MEMORY_INSTRUCTIONS_CLEAR: "memory_instructions_clear",
  // NOTE: Profile → Memory
  MEMORY_ENABLED: "memory_enabled",
  MEMORY_PROFILE: "memory_profile",
  MEMORY_EXCLUSIONS: "memory_exclusions",
  MEMORY_FACTS: "memory_facts",
  MEMORY_ADD_FACT: "memory_add_fact",
  MEMORY_CLEAR: "memory_clear",
  // NOTE: Profile → Calendars
  CALENDAR_CONNECT: "calendar_connect",
  CALENDAR_LIST: "calendar_list",
  // NOTE: Profile / Organization → API keys
  API_KEY_CREATE: "api_key_create",
  API_KEY_LOGS: "api_key_logs",
  // NOTE: Profile → Bot settings
  SLACK_BOT: "slack_bot",
  TEAMS_BOT: "teams_bot",
  MATRIX_BOT: "matrix_bot",
  TELEGRAM_BOT: "telegram_bot",
  // NOTE: Profile / Organization → Token usage, Token & cost usage
  TOKEN_USAGE_FILTERS: "token_usage_filters",
  TOKEN_USAGE_DATE_RANGE: "token_usage_date_range",
  TOKEN_COST_PERIOD: "token_cost_period",
  TOKEN_COST_EXPORT: "token_cost_export",
  TOKEN_COST_TRENDS: "token_cost_trends",
  // NOTE: Organization → General
  ORGANIZATION_NAME: "organization_name",
  ORGANIZATION_EMAIL: "organization_email",
  ORGANIZATION_OWNERS: "organization_owners",
  ORGANIZATION_DEACTIVATE: "organization_deactivate",
  // NOTE: Organization → Projects, Roles, Members
  PROJECT_CREATE: "project_create",
  ROLE_CREATE: "role_create",
  MEMBER_INVITE: "member_invite",
  PENDING_INVITES: "pending_invites",
  BANNED_MEMBERS: "banned_members",
  // NOTE: Organization → Models
  PLATFORM_MODELS: "platform_models",
  CUSTOM_MODELS: "custom_models",
  PENDING_MODEL_APPROVALS: "pending_model_approvals",
  // NOTE: Organization → Integration
  INTEGRATION_BITRIX24: "integration_bitrix24",
  INTEGRATION_AZURE_MES: "integration_azure_mes",
  INTEGRATION_KONTUR_FOCUS: "integration_kontur_focus",
  INTEGRATION_KONTUR_DIADOC: "integration_kontur_diadoc",
  INTEGRATION_JIRA_DC: "integration_jira_dc",
  INTEGRATION_CONFLUENCE_DC: "integration_confluence_dc",
  INTEGRATION_NEXTCLOUD: "integration_nextcloud",
  INTEGRATION_MCP: "integration_mcp",
  MCP_MEMBER_CONNECTIONS: "mcp_member_connections",
  // NOTE: Organization → Connector approvals
  CONNECTOR_APPROVAL_POLICY: "connector_approval_policy",
  // NOTE: Organization → SAML
  SAML_ENTRY_POINT_URL: "saml_entry_point_url",
  SAML_ISSUER: "saml_issuer",
  SAML_DOMAIN: "saml_domain",
  SAML_CERTIFICATE: "saml_certificate",
  SAML_DEFAULT_ROLE: "saml_default_role",
  SAML_DEFAULT_PROJECT: "saml_default_project",
  SAML_PROJECT_MAPPINGS: "saml_project_mappings",
  SAML_ROLE_MAPPINGS: "saml_role_mappings",
  SAML_SEAT_TIERS: "saml_seat_tiers",
  SAML_PROVISIONING_MODE: "saml_provisioning_mode",
  SAML_REQUIRE_SSO: "saml_require_sso",
  SAML_DEPROVISIONING: "saml_deprovisioning",
  SAML_VERIFIED_DOMAINS: "saml_verified_domains",
  // NOTE: Organization → Chat settings
  WEB_SEARCH: "web_search",
  DEEP_RESEARCH: "deep_research",
  DEEP_RESEARCH_CLARIFICATION: "deep_research_clarification",
  PII_ANONYMIZATION: "pii_anonymization",
  PII_INTENT_ROUTING: "pii_intent_routing",
  FINANCIAL_MASKING: "financial_masking",
  PII_ENTITY_RULES: "pii_entity_rules",
  PII_CUSTOM_ENTITIES: "pii_custom_entities",
  PROBE_VALIDATION_MODEL: "probe_validation_model",
  PROBE_REROUTING_MODEL: "probe_rerouting_model",
  PROBE_RETRIEVAL_MODEL: "probe_retrieval_model",
  PII_INTENT_CLASSIFIER_MODEL: "pii_intent_classifier_model",
  PII_TRUSTED_MODEL: "pii_trusted_model",
  PROBE_REASONING_EFFORT: "probe_reasoning_effort",
  PROMPT_IMPROVEMENT_MODEL: "prompt_improvement_model",
  SUMMARY_MODEL: "summary_model",
  SEARCH_EMBEDDING_MODEL: "search_embedding_model",
  // NOTE: Organization → AI call assistant, Syncator API keys
  CUSTOM_JITSI_DOMAINS: "custom_jitsi_domains",
  BOT_JOIN_TIME: "bot_join_time",
  SYNCATOR_API_KEY_CREATE: "syncator_api_key_create",
  // NOTE: Organization → Document recognition
  DOC_RECOGNITION_PROJECT: "doc_recognition_project",
  DOC_RECOGNITION_CONNECTION: "doc_recognition_connection",
  DOC_RECOGNITION_ROOT_FOLDER: "doc_recognition_root_folder",
  DOC_RECOGNITION_ERROR_WEBHOOK: "doc_recognition_error_webhook",
  DOC_RECOGNITION_POLLING: "doc_recognition_polling",
  // NOTE: Organization → Appearance
  PLATFORM_NAME: "platform_name",
  THEME_CUSTOMIZATION: "theme_customization",
  CUSTOM_LOGO: "custom_logo",
  FAVICON: "favicon",
  PRESENTATION_THEME: "presentation_theme",
  EMBED_CODE: "embed_code",
  // NOTE: Organization → Usage limits
  TOKEN_LIMIT_TOTAL: "token_limit_total",
  TOKEN_LIMIT_AGENTS: "token_limit_agents",
  TOKEN_LIMIT_PER_MEMBER: "token_limit_per_member",
  TOKEN_LIMIT_AGENTS_PER_MEMBER: "token_limit_agents_per_member",
  COST_LIMIT_TOTAL: "cost_limit_total",
  COST_LIMIT_AGENTS: "cost_limit_agents",
  COST_LIMIT_PER_MEMBER: "cost_limit_per_member",
  COST_LIMIT_AGENTS_PER_MEMBER: "cost_limit_agents_per_member",
  USAGE_LIMIT_PERIOD: "usage_limit_period",
  PROJECT_LIMITS: "project_limits",
  MODEL_REQUEST_LIMITS: "model_request_limits",
  // NOTE: Organization → Data retention, SIEM export, Memory policy
  DATA_RETENTION_DAYS: "data_retention_days",
  SIEM_ENABLED: "siem_enabled",
  SIEM_TRANSPORT: "siem_transport",
  SIEM_HTTP_ENDPOINT: "siem_http_endpoint",
  SIEM_HTTP_AUTH_HEADER: "siem_http_auth_header",
  SIEM_SYSLOG_HOST: "siem_syslog_host",
  SIEM_SYSLOG_PORT: "siem_syslog_port",
  SIEM_CERTIFICATES: "siem_certificates",
  SIEM_VERIFY_CERTIFICATE: "siem_verify_certificate",
  MEMORY_POLICY_CATEGORIES: "memory_policy_categories",
} as const;

const IDS = GLOBAL_SEARCH_SETTING_IDS;
const GATE = EGlobalSearchGate;

const buildTabOptions = ({
  tabEntry,
  options,
}: IGlobalSearchTabOptionsProps): IGlobalSearchStaticEntry[] =>
  options.map((option: IGlobalSearchSettingOptionDefinition) =>
    buildGlobalSearchSettingOptionEntry({ tabEntry, ...option }),
  );

const TOKEN_USAGE_OPTIONS: readonly IGlobalSearchSettingOptionDefinition[] = [
  { setting: IDS.TOKEN_USAGE_FILTERS, titleKey: "settings:tokenUsage.filter" },
  {
    setting: IDS.TOKEN_USAGE_DATE_RANGE,
    titleKey: "settings:tokenUsage.filter_category.date_range",
  },
];

const TOKEN_COST_USAGE_OPTIONS: readonly IGlobalSearchSettingOptionDefinition[] =
  [
    {
      setting: IDS.TOKEN_COST_PERIOD,
      titleKey: "globalSearchIndex:titles.token_cost_period",
    },
    {
      setting: IDS.TOKEN_COST_EXPORT,
      titleKey: "settings:tokenCostUsage.export.button",
    },
    {
      setting: IDS.TOKEN_COST_TRENDS,
      titleKey: "settings:tokenCostUsage.trends.title",
    },
  ];

const PROFILE_OPTIONS: readonly IGlobalSearchTabOptionsProps[] = [
  {
    tabEntry: PROFILE_TABS.generalInformation,
    options: [
      {
        setting: IDS.FULL_NAME,
        titleKey: "settings:generalInformation.form.fullName",
      },
      {
        setting: IDS.EMAIL,
        titleKey: "settings:generalInformation.form.email",
      },
      {
        setting: IDS.PASSWORD,
        titleKey: "settings:generalInformation.form.password",
        gates: [GATE.PASSWORD_SIGN_IN],
      },
      {
        setting: IDS.SIGN_IN_METHOD,
        titleKey: "settings:generalInformation.form.signInMethod",
      },
      {
        setting: IDS.WHATSAPP_BOT,
        titleKey: "settings:whatsappBot.title",
        gates: [GATE.WHATSAPP_BOT_ENABLED],
      },
      {
        setting: IDS.DELETE_ACCOUNT,
        titleKey: "settings:generalInformation.deleteAccount.title",
        gates: [GATE.ACCOUNT_DELETION_AVAILABLE],
      },
    ],
  },
  {
    tabEntry: PROFILE_TABS.billing,
    options: [
      {
        setting: IDS.BILLING_PLAN,
        titleKey: "globalSearchIndex:titles.billing_plan",
      },
      {
        setting: IDS.BILLING_CYCLE,
        titleKey: "billing:history.table.billingCycle",
      },
      {
        setting: IDS.PAYMENT_METHODS,
        titleKey: "billing:savedPaymentMethods.title",
      },
      { setting: IDS.BILLING_HISTORY, titleKey: "billing:history.title" },
      {
        setting: IDS.CANCEL_SUBSCRIPTION,
        titleKey: "billing:cancelSubscription.title",
      },
    ],
  },
  {
    tabEntry: PROFILE_TABS.preferences,
    options: [
      { setting: IDS.LANGUAGE, titleKey: "preferences:language.title" },
      {
        setting: IDS.THEME,
        titleKey: "preferences:theme.title",
        gates: [GATE.THEME_PICKER],
      },
      {
        setting: IDS.TTS_VOICE,
        titleKey: "preferences:ttsVoice.title",
        gates: [GATE.TTS_VOICE_CHOICE],
      },
      {
        setting: IDS.AUTO_JOIN_MEETINGS,
        titleKey: "preferences:autoJoin.title",
        gates: [GATE.CALL_ASSISTANT_ENABLED],
      },
      {
        setting: IDS.EMAIL_RECAP,
        titleKey: "preferences:emailRecap.title",
        gates: [GATE.CALL_ASSISTANT_ENABLED],
      },
      {
        setting: IDS.NOTIFICATION_CATEGORIES,
        titleKey: "preferences:notifications.categoriesTitle",
      },
    ],
  },
  {
    tabEntry: PROFILE_TABS.memoryInstructions,
    options: [
      {
        setting: IDS.MEMORY_INSTRUCTIONS_ENABLED,
        titleKey: "settings:memoryInstructions.enabledLabel",
      },
      {
        setting: IDS.MEMORY_INSTRUCTIONS_TEXT,
        titleKey: "globalSearchIndex:titles.memory_instructions_text",
      },
      {
        setting: IDS.MEMORY_INSTRUCTIONS_CLEAR,
        titleKey: "settings:memoryInstructions.clear",
      },
    ],
  },
  {
    tabEntry: PROFILE_TABS.memory,
    options: [
      { setting: IDS.MEMORY_ENABLED, titleKey: "settings:memory.enabledLabel" },
      { setting: IDS.MEMORY_PROFILE, titleKey: "settings:memory.profile" },
      {
        setting: IDS.MEMORY_EXCLUSIONS,
        titleKey: "settings:memory.exclusions.title",
      },
      { setting: IDS.MEMORY_FACTS, titleKey: "settings:memory.facts" },
      { setting: IDS.MEMORY_ADD_FACT, titleKey: "settings:memory.addLabel" },
      { setting: IDS.MEMORY_CLEAR, titleKey: "settings:memory.clearAll" },
    ],
  },
  {
    tabEntry: PROFILE_TABS.calendar,
    options: [
      {
        setting: IDS.CALENDAR_CONNECT,
        titleKey: "calendars:table.connectCalendar",
        gates: [GATE.CALL_ASSISTANT_READ],
      },
      {
        setting: IDS.CALENDAR_LIST,
        titleKey: "calendars:table.title",
        gates: [GATE.CALL_ASSISTANT_READ],
      },
    ],
  },
  {
    tabEntry: PROFILE_TABS.apiKeys,
    options: [
      {
        setting: IDS.API_KEY_CREATE,
        titleKey: "settings:apiKeys.createButton",
      },
      {
        setting: IDS.API_KEY_LOGS,
        titleKey: "settings:apiKeys.tabs.logs",
        gates: [GATE.PLATFORM_WEB],
      },
    ],
  },
  {
    tabEntry: PROFILE_TABS.botSettings,
    options: [
      {
        setting: IDS.SLACK_BOT,
        titleKey: "settings:botSettings.slack.name",
        gates: [GATE.SLACK_BOT_ENABLED],
      },
      {
        setting: IDS.TEAMS_BOT,
        titleKey: "settings:botSettings.teams.name",
        gates: [GATE.TEAMS_BOT_ENABLED],
      },
      {
        setting: IDS.MATRIX_BOT,
        titleKey: "settings:matrixBot.title",
        gates: [GATE.MATRIX_BOT_ENABLED],
      },
      // NOTE: NSAI-3874 — web only: the mobile Bot Settings screen has no
      // Telegram tab, the link widget exists on web alone.
      {
        setting: IDS.TELEGRAM_BOT,
        titleKey: "settings:telegramBot.title",
        gates: [GATE.PLATFORM_WEB, GATE.TELEGRAM_BOT_ENABLED],
      },
    ],
  },
  { tabEntry: PROFILE_TABS.tokenUsage, options: TOKEN_USAGE_OPTIONS },
  { tabEntry: PROFILE_TABS.tokenCostUsage, options: TOKEN_COST_USAGE_OPTIONS },
  {
    tabEntry: PROFILE_TABS.about,
    options: [
      { setting: IDS.SERVER, titleKey: "settings:about.server" },
      { setting: IDS.APP_VERSION, titleKey: "settings:about.app_version" },
    ],
  },
];

const ORGANIZATION_OPTIONS: readonly IGlobalSearchTabOptionsProps[] = [
  {
    tabEntry: ORG_TABS.general,
    options: [
      {
        setting: IDS.ORGANIZATION_NAME,
        titleKey: "settings:organization.create.nameLabel",
      },
      {
        setting: IDS.ORGANIZATION_EMAIL,
        titleKey: "settings:organization.create.emailLabel",
      },
      {
        setting: IDS.ORGANIZATION_OWNERS,
        titleKey: "settings:organizationGeneral.ownersLabel",
      },
      {
        setting: IDS.ORGANIZATION_DEACTIVATE,
        titleKey: "settings:delete_organization.title",
        gates: [GATE.ORG_OWNER],
      },
    ],
  },
  {
    tabEntry: ORG_TABS.projects,
    options: [
      {
        setting: IDS.PROJECT_CREATE,
        titleKey: "settings:projects.actions.create",
        gates: [GATE.ORG_OWNER],
      },
    ],
  },
  {
    tabEntry: ORG_TABS.roles,
    options: [
      {
        setting: IDS.ROLE_CREATE,
        titleKey: "settings:roles.actions.create",
        gates: [GATE.ORG_ROLE_WRITE],
      },
    ],
  },
  {
    tabEntry: ORG_TABS.members,
    options: [
      {
        setting: IDS.MEMBER_INVITE,
        titleKey: "settings:members.invite.button",
        gates: [GATE.ORG_MEMBER_INVITE],
      },
      {
        setting: IDS.PENDING_INVITES,
        titleKey: "settings:members.invites.title",
        gates: [GATE.PLATFORM_WEB, GATE.ORG_OWNER],
      },
      {
        setting: IDS.BANNED_MEMBERS,
        titleKey: "settings:members.banned.title",
        gates: [GATE.ORG_OWNER],
      },
    ],
  },
  {
    tabEntry: ORG_TABS.api_keys,
    options: [
      {
        setting: IDS.API_KEY_LOGS,
        titleKey: "settings:apiKeys.tabs.logs",
        gates: [GATE.PLATFORM_WEB],
      },
    ],
  },
  {
    tabEntry: ORG_TABS.models,
    options: [
      {
        setting: IDS.PLATFORM_MODELS,
        titleKey: "settings:organizationModels.platformSection.title",
      },
      {
        setting: IDS.CUSTOM_MODELS,
        titleKey: "settings:organizationModels.customSection.title",
      },
      {
        setting: IDS.PENDING_MODEL_APPROVALS,
        titleKey: "settings:organizationModels.pendingApproval.title",
      },
    ],
  },
  {
    tabEntry: ORG_TABS.integration,
    options: [
      {
        setting: IDS.INTEGRATION_BITRIX24,
        titleKey: "settings:organizationIntegrationConfig.form.bitrix24",
        gates: [GATE.INTEGRATION_BITRIX24_ENABLED],
      },
      {
        setting: IDS.INTEGRATION_AZURE_MES,
        titleKey: "settings:organizationIntegrationConfig.form.mes",
        gates: [GATE.INTEGRATION_AZURE_MES_ENABLED],
      },
      {
        setting: IDS.INTEGRATION_KONTUR_FOCUS,
        titleKey: "settings:organizationIntegrationConfig.form.konturFocus",
        gates: [GATE.INTEGRATION_KONTUR_FOCUS_ENABLED],
      },
      {
        setting: IDS.INTEGRATION_KONTUR_DIADOC,
        titleKey: "settings:organizationIntegrationConfig.form.konturDiadoc",
        gates: [GATE.PLATFORM_WEB, GATE.INTEGRATION_KONTUR_DIADOC_ENABLED],
      },
      {
        setting: IDS.INTEGRATION_JIRA_DC,
        titleKey: "settings:organizationIntegrationConfig.form.jiraDc",
        gates: [GATE.INTEGRATION_JIRA_DC_ENABLED],
      },
      {
        setting: IDS.INTEGRATION_CONFLUENCE_DC,
        titleKey: "settings:organizationIntegrationConfig.form.confluenceDc",
        gates: [GATE.INTEGRATION_CONFLUENCE_DC_ENABLED],
      },
      {
        setting: IDS.INTEGRATION_NEXTCLOUD,
        titleKey: "settings:organizationIntegrationConfig.form.nextcloud",
        gates: [GATE.PLATFORM_WEB, GATE.INTEGRATION_NEXTCLOUD_ENABLED],
      },
      {
        setting: IDS.INTEGRATION_MCP,
        titleKey: "settings:organizationIntegrationConfig.form.mcp",
        gates: [GATE.INTEGRATION_MCPS_ENABLED],
      },
      {
        setting: IDS.MCP_MEMBER_CONNECTIONS,
        titleKey:
          "settings:organizationIntegrationConfig.form.mcpConnectionsTitle",
        gates: [GATE.INTEGRATION_MCPS_ENABLED],
      },
    ],
  },
  {
    tabEntry: ORG_TABS.connector_approval,
    options: [
      {
        setting: IDS.CONNECTOR_APPROVAL_POLICY,
        titleKey: "connectorApproval:org.title",
      },
    ],
  },
  {
    tabEntry: ORG_TABS.saml,
    options: [
      {
        setting: IDS.SAML_ENTRY_POINT_URL,
        titleKey: "settings:organizationSaml.form.entryPointUrlLabel",
      },
      {
        setting: IDS.SAML_ISSUER,
        titleKey: "settings:organizationSaml.form.issuerLabel",
      },
      {
        setting: IDS.SAML_DOMAIN,
        titleKey: "settings:organizationSaml.form.domainLabel",
      },
      {
        setting: IDS.SAML_CERTIFICATE,
        titleKey: "settings:organizationSaml.form.certificateLabel",
      },
      {
        setting: IDS.SAML_DEFAULT_ROLE,
        titleKey: "settings:organizationSaml.form.defaultRoleLabel",
      },
      {
        setting: IDS.SAML_DEFAULT_PROJECT,
        titleKey: "settings:organizationSaml.form.defaultProjectLabel",
      },
      {
        setting: IDS.SAML_PROJECT_MAPPINGS,
        titleKey: "settings:organizationSaml.form.projectMappingsLabel",
      },
      {
        setting: IDS.SAML_ROLE_MAPPINGS,
        titleKey: "settings:organizationSaml.form.roleMappingsLabel",
      },
      {
        setting: IDS.SAML_SEAT_TIERS,
        titleKey: "settings:organizationSaml.form.seatTiersLabel",
        gates: [GATE.SAML_SEAT_TIERS_ENABLED],
      },
      {
        setting: IDS.SAML_PROVISIONING_MODE,
        titleKey: "settings:organizationSaml.form.provisioningModeLabel",
      },
      {
        setting: IDS.SAML_REQUIRE_SSO,
        titleKey: "settings:organizationSaml.form.requireSsoLabel",
      },
      {
        setting: IDS.SAML_DEPROVISIONING,
        titleKey: "settings:organizationSaml.form.deprovisioningEnabledLabel",
      },
      {
        setting: IDS.SAML_VERIFIED_DOMAINS,
        titleKey: "settings:organizationSaml.form.verifiedDomainsLabel",
        gates: [GATE.DOMAIN_VERIFICATION_AVAILABLE],
      },
    ],
  },
  {
    tabEntry: ORG_TABS.chat_settings,
    options: [
      {
        setting: IDS.WEB_SEARCH,
        titleKey: "settings:chat_settings.web_search.title",
      },
      {
        setting: IDS.DEEP_RESEARCH,
        titleKey: "settings:chat_settings.deep_research.title",
      },
      {
        setting: IDS.DEEP_RESEARCH_CLARIFICATION,
        titleKey: "settings:chat_settings.deep_research_clarification.title",
      },
      {
        setting: IDS.PII_ANONYMIZATION,
        titleKey: "settings:chat_settings.pii_anonymization.title",
      },
      {
        setting: IDS.PII_INTENT_ROUTING,
        titleKey: "settings:chat_settings.pii_intent_routing.title",
        gates: [GATE.PII_INTENT_ROUTING_ENABLED],
      },
      {
        setting: IDS.FINANCIAL_MASKING,
        titleKey: "settings:chat_settings.financial_masking.title",
        gates: [GATE.FINANCIAL_MASKING_ENABLED],
      },
      {
        setting: IDS.PII_ENTITY_RULES,
        titleKey: "settings:chat_settings.pii_entity_rules.title",
        gates: [GATE.PII_ADVANCED_RULES_ENABLED],
      },
      {
        setting: IDS.PII_CUSTOM_ENTITIES,
        titleKey: "settings:chat_settings.pii_custom_entities.title",
        gates: [GATE.PII_CUSTOM_ENTITIES_ENABLED],
      },
      {
        setting: IDS.PROBE_VALIDATION_MODEL,
        titleKey:
          "settings:chat_settings.probe_model_settings.probe_validation.title",
      },
      {
        setting: IDS.PROBE_REROUTING_MODEL,
        titleKey:
          "settings:chat_settings.probe_model_settings.probe_rerouting.title",
      },
      {
        setting: IDS.PROBE_RETRIEVAL_MODEL,
        titleKey:
          "settings:chat_settings.probe_model_settings.probe_retrieval.title",
      },
      {
        setting: IDS.PII_INTENT_CLASSIFIER_MODEL,
        titleKey:
          "settings:chat_settings.probe_model_settings.pii_intent_classifier.title",
        gates: [GATE.PII_INTENT_ROUTING_ENABLED],
      },
      {
        setting: IDS.PII_TRUSTED_MODEL,
        titleKey:
          "settings:chat_settings.probe_model_settings.pii_trusted.title",
        gates: [GATE.PII_INTENT_ROUTING_ENABLED],
      },
      {
        setting: IDS.PROBE_REASONING_EFFORT,
        titleKey: "settings:chat_settings.probe_reasoning_effort.title",
      },
      {
        setting: IDS.PROMPT_IMPROVEMENT_MODEL,
        titleKey:
          "settings:chat_settings.prompt_improvement_model.section_title",
      },
      {
        setting: IDS.SUMMARY_MODEL,
        titleKey: "settings:chat_settings.summary_model.section_title",
      },
      {
        setting: IDS.SEARCH_EMBEDDING_MODEL,
        titleKey: "settings:chat_settings.search_embedding_model.section_title",
      },
    ],
  },
  {
    tabEntry: ORG_TABS.call_assistant,
    options: [
      {
        setting: IDS.CUSTOM_JITSI_DOMAINS,
        titleKey:
          "settings:organizationCallAssistant.form.customJitsiDomainsLabel",
      },
      {
        setting: IDS.BOT_JOIN_TIME,
        titleKey: "settings:organizationCallAssistant.form.botJoinTimeLabel",
      },
    ],
  },
  {
    tabEntry: ORG_TABS.syncator_api_keys,
    options: [
      {
        setting: IDS.SYNCATOR_API_KEY_CREATE,
        titleKey: "settings:syncator_api_keys.create",
      },
    ],
  },
  {
    tabEntry: ORG_TABS.doc_recognition,
    options: [
      {
        setting: IDS.DOC_RECOGNITION_PROJECT,
        titleKey: "docRecognition:settings.project",
      },
      {
        setting: IDS.DOC_RECOGNITION_CONNECTION,
        titleKey: "docRecognition:settings.connection",
      },
      {
        setting: IDS.DOC_RECOGNITION_ROOT_FOLDER,
        titleKey: "docRecognition:settings.root_path",
      },
      {
        setting: IDS.DOC_RECOGNITION_ERROR_WEBHOOK,
        titleKey: "docRecognition:settings.error_webhook_url",
      },
      {
        setting: IDS.DOC_RECOGNITION_POLLING,
        titleKey: "docRecognition:settings.is_enabled",
      },
    ],
  },
  {
    tabEntry: ORG_TABS.appearance,
    options: [
      {
        setting: IDS.PLATFORM_NAME,
        titleKey: "settings:appearance.platform_name.title",
        gates: [GATE.PLATFORM_WEB],
      },
      {
        setting: IDS.THEME_CUSTOMIZATION,
        titleKey: "settings:appearance.theme-customization.title",
        gates: [GATE.PLATFORM_WEB],
      },
      {
        setting: IDS.CUSTOM_LOGO,
        titleKey: "settings:appearance.custom_logo.title",
        gates: [GATE.PLATFORM_WEB],
      },
      {
        setting: IDS.FAVICON,
        titleKey: "settings:appearance.favicon.title",
        gates: [GATE.PLATFORM_WEB],
      },
      {
        setting: IDS.PRESENTATION_THEME,
        titleKey: "presentationTheme:section_title",
        gates: [GATE.BRANDED_PRESENTATIONS_ENABLED],
      },
      {
        setting: IDS.EMBED_CODE,
        titleKey: "settings:appearance.theme-customization.embed_code.title",
        gates: [GATE.PLATFORM_WEB],
      },
    ],
  },
  { tabEntry: ORG_TABS.token_usage, options: TOKEN_USAGE_OPTIONS },
  { tabEntry: ORG_TABS.token_cost_usage, options: TOKEN_COST_USAGE_OPTIONS },
  {
    // NOTE: A member granted usage_limits:read sees a read-only overview, and
    // mobile only has that overview — every editable cap is owner-only and
    // web-only (OrganizationUsageLimits/index.tsx:32).
    tabEntry: ORG_TABS.usage_limits,
    options: [
      {
        setting: IDS.TOKEN_LIMIT_TOTAL,
        titleKey: "settings:usageLimits.orgTotalLabel",
      },
      {
        setting: IDS.TOKEN_LIMIT_AGENTS,
        titleKey: "settings:usageLimits.orgAgentsLabel",
      },
      {
        setting: IDS.TOKEN_LIMIT_PER_MEMBER,
        titleKey: "settings:usageLimits.orgPerUserLabel",
      },
      {
        setting: IDS.TOKEN_LIMIT_AGENTS_PER_MEMBER,
        titleKey: "settings:usageLimits.orgAgentsPerUserLabel",
      },
      {
        setting: IDS.COST_LIMIT_TOTAL,
        titleKey: "settings:usageLimits.orgTotalCostLabel",
        gates: [GATE.TOKEN_COST_USAGE_ENABLED],
      },
      {
        setting: IDS.COST_LIMIT_AGENTS,
        titleKey: "settings:usageLimits.orgAgentsCostLabel",
        gates: [GATE.TOKEN_COST_USAGE_ENABLED],
      },
      {
        setting: IDS.COST_LIMIT_PER_MEMBER,
        titleKey: "settings:usageLimits.orgPerUserCostLabel",
        gates: [GATE.TOKEN_COST_USAGE_ENABLED],
      },
      {
        setting: IDS.COST_LIMIT_AGENTS_PER_MEMBER,
        titleKey: "settings:usageLimits.orgAgentsPerUserCostLabel",
        gates: [GATE.TOKEN_COST_USAGE_ENABLED],
      },
      {
        setting: IDS.USAGE_LIMIT_PERIOD,
        titleKey: "settings:usageLimits.periodLabel",
      },
      {
        setting: IDS.PROJECT_LIMITS,
        titleKey: "settings:usageLimits.projectsTitle",
      },
      {
        setting: IDS.MODEL_REQUEST_LIMITS,
        titleKey: "settings:modelTokenLimits.title",
        gates: [GATE.MODEL_TOKEN_LIMITS_ENABLED],
      },
    ].map((option: IGlobalSearchSettingOptionDefinition) => ({
      ...option,
      gates: [GATE.PLATFORM_WEB, GATE.ORG_OWNER, ...(option.gates ?? [])],
    })),
  },
  {
    tabEntry: ORG_TABS.data_retention,
    options: [
      {
        setting: IDS.DATA_RETENTION_DAYS,
        titleKey: "settings:dataRetention.inputLabel",
      },
    ],
  },
  {
    tabEntry: ORG_TABS.siem_export,
    options: [
      {
        setting: IDS.SIEM_ENABLED,
        titleKey: "settings:siemExport.enabledLabel",
      },
      {
        setting: IDS.SIEM_TRANSPORT,
        titleKey: "settings:siemExport.transportLabel",
      },
      {
        setting: IDS.SIEM_HTTP_ENDPOINT,
        titleKey: "settings:siemExport.httpEndpointLabel",
      },
      {
        setting: IDS.SIEM_HTTP_AUTH_HEADER,
        titleKey: "settings:siemExport.httpAuthTitle",
      },
      {
        setting: IDS.SIEM_SYSLOG_HOST,
        titleKey: "settings:siemExport.syslogHostLabel",
      },
      {
        setting: IDS.SIEM_SYSLOG_PORT,
        titleKey: "settings:siemExport.syslogPortLabel",
      },
      {
        setting: IDS.SIEM_CERTIFICATES,
        titleKey: "settings:siemExport.certificatesTitle",
      },
      {
        setting: IDS.SIEM_VERIFY_CERTIFICATE,
        titleKey: "settings:siemExport.rejectUnauthorizedLabel",
      },
    ],
  },
  {
    tabEntry: ORG_TABS.memory_policy,
    options: [
      {
        setting: IDS.MEMORY_POLICY_CATEGORIES,
        titleKey: "globalSearchIndex:titles.memory_policy_categories",
      },
    ],
  },
];

// NOTE: NSAI-6595 — every user-visible control of every settings tab, as a
// SETTINGS_OPTION entry ("setting.<tab>.<setting id>"). Each inherits its tab's
// gates and adds the control's own rendering condition. The History tabs are a
// log with no settings of their own and have no options.
export const GLOBAL_SEARCH_SETTING_OPTION_ENTRIES: readonly IGlobalSearchStaticEntry[] =
  [...PROFILE_OPTIONS, ...ORGANIZATION_OPTIONS].flatMap(buildTabOptions);
